import { useState, useEffect, useRef } from 'react';
import { PDFDocumentProxy } from 'pdfjs-dist';
import { loadPDFDocument } from '../loader';
import { PDFError } from '../types';

interface UsePDFLoaderResult {
  pdfDoc: PDFDocumentProxy | null;
  loading: boolean;
  error: string | null;
}

export function usePDFLoader(file: File): UsePDFLoaderResult {
  const [pdfDoc, setPdfDoc] = useState<PDFDocumentProxy | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let currentDoc: PDFDocumentProxy | null = null;

    const loadPDF = async () => {
      try {
        setLoading(true);
        setError(null);

        // Cleanup previous document
        if (pdfDoc) {
          await pdfDoc.destroy();
        }

        // Load new document
        currentDoc = await loadPDFDocument(file);
        
        if (!mountedRef.current) {
          await currentDoc.destroy();
          return;
        }

        setPdfDoc(currentDoc);
      } catch (err) {
        console.error('PDF loading error:', err);
        if (mountedRef.current) {
          setError(err instanceof PDFError ? err.message : 'Failed to load PDF');
        }
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    };

    loadPDF();

    return () => {
      // Cleanup on unmount or when file changes
      if (currentDoc) {
        currentDoc.destroy().catch(console.error);
      }
    };
  }, [file]);

  return { pdfDoc, loading, error };
}